import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faFilter as faFilterSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { faFilter as faFilterRegular } from "@fortawesome/pro-regular-svg-icons";
import styled, { css } from "styled-components";
import { LinkButton, PlayButton } from "../../common/CommonStyles";
import {
  POPUP_MENU_ITEM_SX,
  PopupMenu,
  PopupMenuCheck,
} from "../../common/PopupMenu";
import {
  COLOR_BORDER_DEFAULT,
  COLOR_SURFACE_CONTAINER,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
} from "../../common/colors";
import { SPACE_LG, SPACE_MD, SPACE_SM, SPACE_XL } from "../../common/spacing";

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
  font-size: 13px;
  color: ${COLOR_TEXT_SECONDARY};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${COLOR_TEXT_PRIMARY};
`;

const tableLinks = css`
  td a:not([class]) {
    color: ${COLOR_TEXT_PRIMARY};
    font-weight: 500;
    text-decoration: underline;
    text-decoration-color: ${COLOR_BORDER_DEFAULT};
    text-underline-offset: 3px;
  }
  td a:not([class]):hover {
    text-decoration-color: currentColor;
  }
`;

const stripedRows = css`
  th:first-child,
  td:first-child {
    padding-left: ${SPACE_MD}px;
  }
  th:last-child,
  td:last-child {
    padding-right: ${SPACE_MD}px;
  }
  tbody tr:nth-child(even) td {
    background-color: ${COLOR_SURFACE_CONTAINER};
  }
`;

const StyledActivityTable = styled.table`
  width: 100%;
  font-size: 13px;
  font-weight: 500;
  color: ${COLOR_TEXT_PRIMARY};
  th {
    padding: 0 ${SPACE_LG}px 6px 0;
    font-weight: 400;
    color: ${COLOR_TEXT_SECONDARY};
    white-space: nowrap;
  }
  td {
    padding: 6px ${SPACE_LG}px 6px 0;
    vertical-align: middle;
    white-space: nowrap;
  }
  ${stripedRows}
  ${tableLinks}
`;

export const SecondaryCell = styled.td`
  color: ${COLOR_TEXT_SECONDARY};
`;

const StyledDataTable = styled.table`
  width: 100%;
  font-size: 14px;
  color: ${COLOR_TEXT_SECONDARY};
  th,
  td {
    padding-left: ${SPACE_XL}px;
    text-align: right;
    white-space: nowrap;
  }
  th {
    padding-bottom: ${SPACE_MD}px;
    font-weight: 600;
    color: ${COLOR_TEXT_PRIMARY};
    vertical-align: bottom;
  }
  td {
    padding-top: ${SPACE_SM}px;
    padding-bottom: ${SPACE_SM}px;
  }
  th:first-child,
  td:first-child {
    width: 100%;
    text-align: left;
  }
  ${stripedRows}
  ${tableLinks}
`;

type TableProps = { className?: string; children: React.ReactNode };

const responsiveTable =
  (Table: typeof StyledActivityTable) =>
  ({ className, children }: TableProps) => (
    <div className={"table-responsive " + (className ?? "")}>
      <Table>{children}</Table>
    </div>
  );

export const ActivityTable = responsiveTable(StyledActivityTable);

export const DataTable = responsiveTable(StyledDataTable);

export const DashboardCtaButton = styled(PlayButton)`
  width: auto;
  max-width: none;
  margin: 0;
  padding: ${SPACE_MD}px ${SPACE_LG}px;
  font-size: 14px;
`;

const SortIndicator = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  margin-left: 6px;
  border-radius: 50%;
  background-color: ${COLOR_SURFACE_CONTAINER};
  font-size: 11px;
  vertical-align: middle;
`;

export const SortArrow = ({ dir }: { dir: "asc" | "desc" }) => (
  <SortIndicator>
    <FontAwesomeIcon icon={dir === "asc" ? faArrowUp : faArrowDown} />
  </SortIndicator>
);

export function useSortableColumns<K extends string>() {
  const [sortKey, setSortKey] = React.useState<K | null>(null);
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc");

  const thProps = (key: K) => ({
    scope: "col" as const,
    style: { cursor: "pointer" },
    onClick: () => {
      if (sortKey === key) {
        setSortDir(sortDir === "asc" ? "desc" : "asc");
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
  });

  const indicator = (key: K) => sortKey === key && <SortArrow dir={sortDir} />;

  const sortRows = <T,>(rows: T[], compare: (a: T, b: T, key: K) => number) =>
    sortKey
      ? [...rows].sort(
          (a, b) => (sortDir === "asc" ? 1 : -1) * compare(a, b, sortKey)
        )
      : rows;

  return { thProps, indicator, sortRows };
}

const TooltipLabel = styled.span`
  cursor: help;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
`;

export const LabelWithTooltip = ({
  tooltip,
  children,
}: {
  tooltip: string;
  children: React.ReactNode;
}) => (
  <OverlayTrigger
    placement="top"
    delay={{ show: 250, hide: 400 }}
    overlay={<Tooltip id={`tooltip-top`}>{tooltip}</Tooltip>}
  >
    <TooltipLabel>{children}</TooltipLabel>
  </OverlayTrigger>
);

const IconToggle = styled(LinkButton)`
  display: inline-flex;
  align-items: center;
  line-height: 1;
  vertical-align: middle;
  svg {
    display: block;
    font-size: 0.65em;
    color: ${COLOR_TEXT_SECONDARY};
  }
`;

export const CompletedFilter = ({
  showAll,
  toggleUrl,
}: {
  showAll?: boolean;
  toggleUrl?: string;
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  return (
    <>
      <IconToggle
        type="button"
        aria-haspopup="menu"
        aria-expanded={anchorEl !== null}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <FontAwesomeIcon
          icon={showAll ? faFilterRegular : faFilterSolid}
          title={t("dashboard_show_incomplete_lessons")}
        />
      </IconToggle>
      <PopupMenu
        open={anchorEl !== null}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem component="a" href={toggleUrl} sx={POPUP_MENU_ITEM_SX}>
          {showAll && <PopupMenuCheck />}
          {t("dashboard_show_incomplete_lessons")}
        </MenuItem>
      </PopupMenu>
    </>
  );
};
